import React, { useState, useEffect, useCallback } from 'react';
import { 
  BarChart3, TrendingUp, PoundSterling, Users, FileText, 
  Download, ChevronRight, ChevronDown, Menu, CalendarIcon,
  ArrowUpRight, ArrowDownRight, Clock, CheckCircle, Loader2,
  Receipt, CreditCard, UserMinus, UserPlus, AlertCircle,
  Filter, RefreshCw, Eye, Briefcase
} from 'lucide-react';
import { format, startOfYear, endOfYear } from 'date-fns';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Button } from '../components/ui/button';
import * as api from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Get current year defaults
const currentYear = new Date().getFullYear();
const defaultFromDate = startOfYear(new Date(currentYear, 0, 1));
const defaultToDate = endOfYear(new Date(currentYear, 11, 31));

// Format currency helper
const formatCurrency = (value) => {
  return `£${(value || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Date Picker Component for individual reports
const ReportDatePicker = ({ fromDate, toDate, onFromChange, onToChange, onApply, isLoading }) => {
  return (
    <div className="flex items-center gap-2 mb-4 p-3 bg-white rounded-lg border border-zinc-200">
      <span className="text-sm text-zinc-600 font-medium">Filter:</span>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="h-8 text-xs px-2 border-zinc-300"
            data-testid="report-from-date"
          >
            <CalendarIcon className="mr-1 h-3 w-3" />
            {fromDate ? format(fromDate, 'dd MMM yyyy') : 'From'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-white z-50" align="start">
          <Calendar
            mode="single"
            selected={fromDate}
            onSelect={onFromChange}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      
      <span className="text-zinc-400">→</span>
      
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="h-8 text-xs px-2 border-zinc-300"
            data-testid="report-to-date"
          >
            <CalendarIcon className="mr-1 h-3 w-3" />
            {toDate ? format(toDate, 'dd MMM yyyy') : 'To'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-white z-50" align="start">
          <Calendar
            mode="single"
            selected={toDate}
            onSelect={onToChange}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      
      <Button
        onClick={onApply}
        size="sm"
        className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700"
        disabled={isLoading}
        data-testid="apply-date-filter"
      >
        {isLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Apply'}
      </Button>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'emerald', onClick }) => {
  const colorClasses = {
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600', icon: 'text-emerald-500' },
    blue: { bg: 'bg-blue-50', text: 'text-blue-600', icon: 'text-blue-500' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-600', icon: 'text-amber-500' },
    red: { bg: 'bg-red-50', text: 'text-red-600', icon: 'text-red-500' },
    purple: { bg: 'bg-purple-50', text: 'text-purple-600', icon: 'text-purple-500' },
    zinc: { bg: 'bg-zinc-100', text: 'text-zinc-700', icon: 'text-zinc-500' }
  };
  const colors = colorClasses[color] || colorClasses.emerald;

  return (
    <div 
      className={`bg-white rounded-xl p-4 shadow-sm border border-zinc-100 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={onClick}
      data-testid={`stat-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-zinc-500">{title}</p>
          <p className={`text-2xl font-bold mt-1 ${colors.text}`}>{value}</p>
          {(subtitle || trendValue) && (
            <p className="text-xs text-zinc-400 mt-1 flex items-center gap-1">
              {trend === 'up' && <ArrowUpRight size={12} className="text-emerald-500" />}
              {trend === 'down' && <ArrowDownRight size={12} className="text-red-500" />}
              {trendValue && <span className={trend === 'up' ? 'text-emerald-600' : trend === 'down' ? 'text-red-600' : ''}>{trendValue}</span>}
              {subtitle && <span>{subtitle}</span>}
            </p>
          )}
        </div>
        <div className={`p-3 rounded-xl ${colors.bg}`}>
          <Icon className={colors.icon} size={22} />
        </div>
      </div>
    </div>
  );
};

// Mini Bar Chart
const MiniBarChart = ({ data, valueKey, labelKey, color = '#10b981' }) => {
  const maxValue = Math.max(...data.map(d => d[valueKey] || 0), 1);
  
  return (
    <div className="flex items-end gap-1 h-20">
      {data.slice(-12).map((item, idx) => (
        <div key={idx} className="flex-1 flex flex-col items-center gap-1">
          <div 
            className="w-full rounded-t transition-all hover:opacity-80"
            style={{ 
              height: `${Math.max((item[valueKey] || 0) / maxValue * 100, 4)}%`,
              backgroundColor: color
            }}
            title={`${item[labelKey]}: ${item[valueKey]}`}
          />
          <span className="text-[9px] text-zinc-400 truncate w-full text-center">
            {item[labelKey]?.slice(-2)}
          </span>
        </div>
      ))}
    </div>
  );
};

// Individual Report Components with their own date filters

// Active Customers Report
const ActiveCustomersReport = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = api.getToken();
      const res = await fetch(`${API_URL}/api/reports/active-customers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setData(await res.json());
    } catch (err) {
      console.error('Error loading active customers:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  if (isLoading) return <div className="flex justify-center p-4"><Loader2 className="animate-spin text-zinc-400" /></div>;
  if (!data) return <p className="text-zinc-500 text-sm">No data available</p>;

  return (
    <div className="space-y-4">
      <p className="text-xs text-zinc-500 italic">Showing all active customers (not date filtered)</p>
      <div className="grid grid-cols-3 gap-3">
        <div className="text-center p-3 bg-white rounded-lg">
          <p className="text-2xl font-bold text-blue-600">{data.total_active}</p>
          <p className="text-xs text-zinc-500">Total Active</p>
        </div>
        <div className="text-center p-3 bg-white rounded-lg">
          <p className="text-2xl font-bold text-emerald-600">{formatCurrency(data.total_job_value)}</p>
          <p className="text-xs text-zinc-500">Total Value</p>
        </div>
        <div className="text-center p-3 bg-white rounded-lg">
          <p className="text-2xl font-bold text-purple-600">
            {formatCurrency(data.total_job_value / (data.total_active || 1))}
          </p>
          <p className="text-xs text-zinc-500">Avg per Customer</p>
        </div>
      </div>
      <div className="bg-white rounded-lg p-3">
        <p className="text-sm font-medium text-zinc-700 mb-2">Top Customers by Value</p>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {data.customers?.slice(0, 10).map((c, idx) => (
            <div key={c.id || idx} className="flex items-center justify-between text-sm">
              <span className="text-zinc-700 truncate flex-1">{c.name}</span>
              <span className="font-medium text-zinc-900">{formatCurrency(c.total_job_value)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Customer Growth Report - Enhanced with bulk import detection
const CustomerGrowthReport = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showBulkImports, setShowBulkImports] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const loadData = useCallback(async (from, to, includeBulk = false) => {
    setIsLoading(true);
    try {
      const token = api.getToken();
      let url = `${API_URL}/api/reports/customer-growth?months=36&exclude_bulk_imports=${!includeBulk}`;
      if (from) url += `&start_date=${format(from, 'yyyy-MM-dd')}`;
      if (to) url += `&end_date=${format(to, 'yyyy-MM-dd')}`;
      
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setData(await res.json());
    } catch (err) {
      console.error('Error loading customer growth:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadData(fromDate, toDate, showBulkImports); }, []);

  const handleApply = () => loadData(fromDate, toDate, showBulkImports);
  
  const toggleBulkImports = () => {
    const newValue = !showBulkImports;
    setShowBulkImports(newValue);
    loadData(fromDate, toDate, newValue);
  };

  // Format month for display (e.g., "2024-01" -> "Jan 2024")
  const formatMonth = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  if (isLoading) return <div className="flex justify-center p-4"><Loader2 className="animate-spin text-zinc-400" /></div>;
  if (!data) return <p className="text-zinc-500 text-sm">No data available</p>;

  // Filter out bulk import months for display unless toggled
  const displayMonths = showBulkImports 
    ? data.months 
    : data.months?.filter(m => !m.is_bulk_import) || [];

  // Calculate max for chart scaling
  const maxSignups = Math.max(...displayMonths.map(m => m.signups), 1);

  return (
    <div className="space-y-4">
      {/* Date Filter */}
      <div className="flex items-center gap-2 p-3 bg-white rounded-lg border border-zinc-200 flex-wrap">
        <span className="text-sm text-zinc-600 font-medium">Filter:</span>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="h-8 text-xs px-2 border-zinc-300" data-testid="growth-from-date">
              <CalendarIcon className="mr-1 h-3 w-3" />
              {fromDate ? format(fromDate, 'MMM yyyy') : 'All time'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-white z-50" align="start">
            <Calendar mode="single" selected={fromDate} onSelect={setFromDate} initialFocus />
          </PopoverContent>
        </Popover>
        <span className="text-zinc-400">→</span>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="h-8 text-xs px-2 border-zinc-300" data-testid="growth-to-date">
              <CalendarIcon className="mr-1 h-3 w-3" />
              {toDate ? format(toDate, 'MMM yyyy') : 'Now'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-white z-50" align="start">
            <Calendar mode="single" selected={toDate} onSelect={setToDate} initialFocus />
          </PopoverContent>
        </Popover>
        <Button onClick={handleApply} size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700" disabled={isLoading}>
          {isLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Apply'}
        </Button>
      </div>

      {/* Bulk Import Notice */}
      {data.bulk_import_months?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle size={16} className="text-amber-600" />
              <span className="text-sm text-amber-800">
                <strong>{data.bulk_import_total}</strong> customers from bulk imports detected ({data.bulk_import_months.length} month{data.bulk_import_months.length > 1 ? 's' : ''})
              </span>
            </div>
            <button
              onClick={toggleBulkImports}
              className="text-xs text-amber-700 hover:text-amber-900 underline"
            >
              {showBulkImports ? 'Hide bulk imports' : 'Show bulk imports'}
            </button>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="text-center p-3 bg-white rounded-lg border border-zinc-100">
          <p className="text-2xl font-bold text-emerald-600">{data.total_signups}</p>
          <p className="text-xs text-zinc-500">Total Signups</p>
        </div>
        <div className="text-center p-3 bg-white rounded-lg border border-zinc-100">
          <p className="text-2xl font-bold text-red-600">{data.total_cancellations}</p>
          <p className="text-xs text-zinc-500">Cancellations</p>
        </div>
        <div className="text-center p-3 bg-white rounded-lg border border-zinc-100">
          <p className={`text-2xl font-bold ${data.net_growth >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {data.net_growth >= 0 ? '+' : ''}{data.net_growth}
          </p>
          <p className="text-xs text-zinc-500">Net Growth</p>
        </div>
        <div className="text-center p-3 bg-white rounded-lg border border-zinc-100">
          <p className="text-2xl font-bold text-blue-600">{data.avg_monthly_signups}</p>
          <p className="text-xs text-zinc-500">Avg/Month</p>
        </div>
      </div>

      {/* Monthly Data Table */}
      <div className="bg-white rounded-lg overflow-hidden border border-zinc-100">
        <div className="p-3 border-b border-zinc-100 bg-zinc-50">
          <h3 className="text-sm font-semibold text-zinc-700">Customer Signups by Month</h3>
        </div>
        <div className="max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 sticky top-0">
              <tr>
                <th className="text-left p-3 font-medium text-zinc-600">Month</th>
                <th className="text-right p-3 font-medium text-zinc-600">Signups</th>
                <th className="text-right p-3 font-medium text-zinc-600">Left</th>
                <th className="text-right p-3 font-medium text-zinc-600">Net</th>
                <th className="text-right p-3 font-medium text-zinc-600 w-24">Change</th>
                <th className="p-3 w-32">Trend</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {displayMonths.slice().reverse().map((m, idx) => (
                <tr key={m.month} className={`hover:bg-zinc-50 ${m.is_bulk_import ? 'bg-amber-50' : ''}`}>
                  <td className="p-3 text-zinc-900 font-medium">
                    {formatMonth(m.month)}
                    {m.is_bulk_import && (
                      <span className="ml-2 text-xs bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded">Import</span>
                    )}
                  </td>
                  <td className="p-3 text-right text-emerald-600 font-semibold">{m.signups}</td>
                  <td className="p-3 text-right text-red-600">{m.cancellations}</td>
                  <td className={`p-3 text-right font-medium ${m.net_growth >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {m.net_growth >= 0 ? '+' : ''}{m.net_growth}
                  </td>
                  <td className="p-3 text-right">
                    {m.mom_change !== null ? (
                      <span className={`inline-flex items-center gap-1 ${m.mom_change >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {m.mom_change >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                        {Math.abs(m.mom_change)}%
                      </span>
                    ) : (
                      <span className="text-zinc-400">—</span>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="h-4 bg-zinc-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all ${m.is_bulk_import ? 'bg-amber-400' : 'bg-emerald-500'}`}
                        style={{ width: `${(m.signups / maxSignups) * 100}%` }}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Visual Chart */}
      <div className="bg-white rounded-lg p-4 border border-zinc-100">
        <h3 className="text-sm font-semibold text-zinc-700 mb-3">Monthly Trend</h3>
        <div className="flex items-end gap-1 h-32">
          {displayMonths.slice(-24).map((m, idx) => (
            <div key={m.month} className="flex-1 flex flex-col items-center gap-1 group relative">
              <div 
                className={`w-full rounded-t transition-all hover:opacity-80 ${m.is_bulk_import ? 'bg-amber-400' : 'bg-emerald-500'}`}
                style={{ height: `${Math.max((m.signups / maxSignups) * 100, 4)}%` }}
              />
              <span className="text-[8px] text-zinc-400 truncate w-full text-center">
                {m.month?.slice(-2)}
              </span>
              {/* Tooltip */}
              <div className="absolute bottom-full mb-2 hidden group-hover:block bg-zinc-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                {formatMonth(m.month)}: {m.signups} signups
                {m.is_bulk_import && ' (bulk import)'}
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-center gap-4 mt-3 text-xs text-zinc-500">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-emerald-500 rounded" />
            <span>Regular signups</span>
          </div>
          {data.bulk_import_months?.length > 0 && showBulkImports && (
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-amber-400 rounded" />
              <span>Bulk imports</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Customer Churn Report
const CustomerChurnReport = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = api.getToken();
      const res = await fetch(`${API_URL}/api/reports/customer-churn`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setData(await res.json());
    } catch (err) {
      console.error('Error loading churn data:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  if (isLoading) return <div className="flex justify-center p-4"><Loader2 className="animate-spin text-zinc-400" /></div>;
  if (!data) return <p className="text-zinc-500 text-sm">No data available</p>;

  return (
    <div className="space-y-4">
      <p className="text-xs text-zinc-500 italic">Overall churn metrics</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="text-center p-3 bg-white rounded-lg">
          <p className="text-2xl font-bold text-zinc-700">{data.total_customers}</p>
          <p className="text-xs text-zinc-500">Total Customers</p>
        </div>
        <div className="text-center p-3 bg-white rounded-lg">
          <p className="text-2xl font-bold text-emerald-600">{data.active_customers}</p>
          <p className="text-xs text-zinc-500">Active</p>
        </div>
        <div className="text-center p-3 bg-white rounded-lg">
          <p className="text-2xl font-bold text-red-600">{data.churn_rate}%</p>
          <p className="text-xs text-zinc-500">Churn Rate</p>
        </div>
        <div className="text-center p-3 bg-white rounded-lg">
          <p className="text-2xl font-bold text-blue-600">{data.retention_rate}%</p>
          <p className="text-xs text-zinc-500">Retention Rate</p>
        </div>
      </div>
      <div className="bg-white rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-zinc-600">Recent Churns (30 days)</span>
          <span className="font-bold text-red-600">{data.recent_churns_30d}</span>
        </div>
        <div className="h-3 bg-zinc-100 rounded-full overflow-hidden">
          <div 
            className="h-full bg-emerald-500 rounded-full transition-all"
            style={{ width: `${data.retention_rate}%` }}
          />
        </div>
        <p className="text-xs text-zinc-400 mt-1 text-right">{data.retention_rate}% customers retained</p>
      </div>
    </div>
  );
};

// Total Revenue Report (with date filter)
const TotalRevenueReport = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fromDate, setFromDate] = useState(defaultFromDate);
  const [toDate, setToDate] = useState(defaultToDate);

  const loadData = useCallback(async (from, to) => {
    setIsLoading(true);
    try {
      const token = api.getToken();
      const startDate = format(from, 'yyyy-MM-dd');
      const endDate = format(to, 'yyyy-MM-dd');
      const res = await fetch(`${API_URL}/api/reports/total-revenue?start_date=${startDate}&end_date=${endDate}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setData(await res.json());
    } catch (err) {
      console.error('Error loading revenue:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadData(fromDate, toDate); }, []);

  const handleApply = () => loadData(fromDate, toDate);

  return (
    <div className="space-y-4">
      <ReportDatePicker
        fromDate={fromDate}
        toDate={toDate}
        onFromChange={setFromDate}
        onToChange={setToDate}
        onApply={handleApply}
        isLoading={isLoading}
      />
      
      {isLoading ? (
        <div className="flex justify-center p-4"><Loader2 className="animate-spin text-zinc-400" /></div>
      ) : !data ? (
        <p className="text-zinc-500 text-sm">No data available</p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="text-center p-3 bg-white rounded-lg">
              <p className="text-2xl font-bold text-emerald-600">{formatCurrency(data.total_revenue)}</p>
              <p className="text-xs text-zinc-500">Total Revenue</p>
            </div>
            <div className="text-center p-3 bg-white rounded-lg">
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(data.completed_revenue)}</p>
              <p className="text-xs text-zinc-500">Completed</p>
            </div>
            <div className="text-center p-3 bg-white rounded-lg">
              <p className="text-2xl font-bold text-amber-600">{formatCurrency(data.pending_revenue)}</p>
              <p className="text-xs text-zinc-500">Pending</p>
            </div>
            <div className="text-center p-3 bg-white rounded-lg">
              <p className="text-2xl font-bold text-purple-600">{formatCurrency(data.average_job_value)}</p>
              <p className="text-xs text-zinc-500">Avg Job Value</p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-3">
            <p className="text-sm font-medium text-zinc-700 mb-3">Revenue by Zone</p>
            <div className="space-y-2">
              {data.revenue_by_zone?.slice(0, 6).map((zone, idx) => (
                <div key={zone.zone || idx} className="flex items-center gap-3">
                  <span className="text-sm text-zinc-600 w-24 truncate">{zone.zone}</span>
                  <div className="flex-1 h-2 bg-zinc-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-emerald-500 rounded-full"
                      style={{ width: `${(zone.revenue / (data.revenue_by_zone[0]?.revenue || 1)) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-zinc-900 w-20 text-right">{formatCurrency(zone.revenue)}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Revenue by Worker Report (with date filter)
const RevenueByWorkerReport = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fromDate, setFromDate] = useState(defaultFromDate);
  const [toDate, setToDate] = useState(defaultToDate);

  const loadData = useCallback(async (from, to) => {
    setIsLoading(true);
    try {
      const token = api.getToken();
      const startDate = format(from, 'yyyy-MM-dd');
      const endDate = format(to, 'yyyy-MM-dd');
      const res = await fetch(`${API_URL}/api/reports/revenue-by-worker?start_date=${startDate}&end_date=${endDate}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setData(await res.json());
    } catch (err) {
      console.error('Error loading worker revenue:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadData(fromDate, toDate); }, []);

  const handleApply = () => loadData(fromDate, toDate);

  return (
    <div className="space-y-4">
      <ReportDatePicker
        fromDate={fromDate}
        toDate={toDate}
        onFromChange={setFromDate}
        onToChange={setToDate}
        onApply={handleApply}
        isLoading={isLoading}
      />
      
      {isLoading ? (
        <div className="flex justify-center p-4"><Loader2 className="animate-spin text-zinc-400" /></div>
      ) : !data ? (
        <p className="text-zinc-500 text-sm">No data available</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div className="text-center p-3 bg-white rounded-lg">
              <p className="text-2xl font-bold text-purple-600">{formatCurrency(data.total_revenue)}</p>
              <p className="text-xs text-zinc-500">Total Revenue</p>
            </div>
            <div className="text-center p-3 bg-white rounded-lg">
              <p className="text-2xl font-bold text-blue-600">{data.total_jobs}</p>
              <p className="text-xs text-zinc-500">Total Jobs</p>
            </div>
          </div>
          <div className="bg-white rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50">
                <tr>
                  <th className="text-left p-3 font-medium text-zinc-600">Worker</th>
                  <th className="text-right p-3 font-medium text-zinc-600">Jobs</th>
                  <th className="text-right p-3 font-medium text-zinc-600">Revenue</th>
                  <th className="text-right p-3 font-medium text-zinc-600">Completion</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {data.workers?.map((w, idx) => (
                  <tr key={w.worker || idx} className="hover:bg-zinc-50">
                    <td className="p-3 text-zinc-900">{w.worker}</td>
                    <td className="p-3 text-right text-zinc-600">{w.job_count}</td>
                    <td className="p-3 text-right font-medium text-emerald-600">{formatCurrency(w.total_revenue)}</td>
                    <td className="p-3 text-right text-zinc-600">{w.completion_rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

// Owing Customers Report
const OwingCustomersReport = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = api.getToken();
      const res = await fetch(`${API_URL}/api/reports/owing-customers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setData(await res.json());
    } catch (err) {
      console.error('Error loading owing customers:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  if (isLoading) return <div className="flex justify-center p-4"><Loader2 className="animate-spin text-zinc-400" /></div>;
  if (!data) return <p className="text-zinc-500 text-sm">No data available</p>;

  return (
    <div className="space-y-4">
      <p className="text-xs text-zinc-500 italic">Current outstanding balances</p>
      <div className="grid grid-cols-3 gap-3">
        <div className="text-center p-3 bg-white rounded-lg">
          <p className="text-2xl font-bold text-amber-600">{formatCurrency(data.total_owed)}</p>
          <p className="text-xs text-zinc-500">Total Owed</p>
        </div>
        <div className="text-center p-3 bg-white rounded-lg">
          <p className="text-2xl font-bold text-blue-600">{data.customer_count}</p>
          <p className="text-xs text-zinc-500">Customers</p>
        </div>
        <div className="text-center p-3 bg-white rounded-lg">
          <p className="text-2xl font-bold text-purple-600">{formatCurrency(data.average_debt)}</p>
          <p className="text-xs text-zinc-500">Avg Debt</p>
        </div>
      </div>
      <div className="bg-white rounded-lg p-3">
        <p className="text-sm font-medium text-zinc-700 mb-2">Age of Debt</p>
        <div className="grid grid-cols-4 gap-2 text-center">
          {Object.entries(data.age_breakdown || {}).map(([age, amount]) => (
            <div key={age} className="p-2 bg-zinc-50 rounded">
              <p className="text-xs text-zinc-500">{age} days</p>
              <p className="font-semibold text-zinc-900">{formatCurrency(amount)}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-white rounded-lg p-3">
        <p className="text-sm font-medium text-zinc-700 mb-2">Top Owing Customers</p>
        <div className="space-y-2 max-h-40 overflow-y-auto">
          {data.customers?.slice(0, 8).map((c, idx) => (
            <div key={c.id || idx} className="flex items-center justify-between text-sm">
              <span className="text-zinc-700 truncate flex-1">{c.name}</span>
              <span className="font-medium text-amber-600">{formatCurrency(c.balance)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Payments Report (with date filter)
const PaymentsReport = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fromDate, setFromDate] = useState(defaultFromDate);
  const [toDate, setToDate] = useState(defaultToDate);

  const loadData = useCallback(async (from, to) => {
    setIsLoading(true);
    try {
      const token = api.getToken();
      const startDate = format(from, 'yyyy-MM-dd');
      const endDate = format(to, 'yyyy-MM-dd');
      const res = await fetch(`${API_URL}/api/reports/payments?start_date=${startDate}&end_date=${endDate}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setData(await res.json());
    } catch (err) {
      console.error('Error loading payments:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadData(fromDate, toDate); }, []);

  const handleApply = () => loadData(fromDate, toDate);

  return (
    <div className="space-y-4">
      <ReportDatePicker
        fromDate={fromDate}
        toDate={toDate}
        onFromChange={setFromDate}
        onToChange={setToDate}
        onApply={handleApply}
        isLoading={isLoading}
      />
      
      {isLoading ? (
        <div className="flex justify-center p-4"><Loader2 className="animate-spin text-zinc-400" /></div>
      ) : !data ? (
        <p className="text-zinc-500 text-sm">No data available</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div className="text-center p-3 bg-white rounded-lg">
              <p className="text-2xl font-bold text-green-600">{formatCurrency(data.total_amount)}</p>
              <p className="text-xs text-zinc-500">Total Payments</p>
            </div>
            <div className="text-center p-3 bg-white rounded-lg">
              <p className="text-2xl font-bold text-blue-600">{data.total_payments}</p>
              <p className="text-xs text-zinc-500">Payment Count</p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-3">
            <p className="text-sm font-medium text-zinc-700 mb-2">Monthly Payments</p>
            <MiniBarChart data={data.by_month || []} valueKey="total" labelKey="month" color="#22c55e" />
          </div>
          {data.by_method?.length > 0 && (
            <div className="bg-white rounded-lg p-3">
              <p className="text-sm font-medium text-zinc-700 mb-2">By Payment Method</p>
              <div className="space-y-2">
                {data.by_method.map((m, idx) => (
                  <div key={m.method || idx} className="flex items-center justify-between text-sm">
                    <span className="text-zinc-600">{m.method}</span>
                    <span className="font-medium">{formatCurrency(m.total)} ({m.count})</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Invoices Report (with date filter)
const InvoicesReport = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fromDate, setFromDate] = useState(defaultFromDate);
  const [toDate, setToDate] = useState(defaultToDate);

  const loadData = useCallback(async (from, to) => {
    setIsLoading(true);
    try {
      const token = api.getToken();
      const startDate = format(from, 'yyyy-MM-dd');
      const endDate = format(to, 'yyyy-MM-dd');
      const res = await fetch(`${API_URL}/api/reports/invoices?start_date=${startDate}&end_date=${endDate}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setData(await res.json());
    } catch (err) {
      console.error('Error loading invoices:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadData(fromDate, toDate); }, []);

  const handleApply = () => loadData(fromDate, toDate);

  return (
    <div className="space-y-4">
      <ReportDatePicker
        fromDate={fromDate}
        toDate={toDate}
        onFromChange={setFromDate}
        onToChange={setToDate}
        onApply={handleApply}
        isLoading={isLoading}
      />
      
      {isLoading ? (
        <div className="flex justify-center p-4"><Loader2 className="animate-spin text-zinc-400" /></div>
      ) : !data ? (
        <p className="text-zinc-500 text-sm">No data available</p>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-3 bg-white rounded-lg">
              <p className="text-2xl font-bold text-indigo-600">{formatCurrency(data.total_amount)}</p>
              <p className="text-xs text-zinc-500">Total Invoiced</p>
            </div>
            <div className="text-center p-3 bg-white rounded-lg">
              <p className="text-2xl font-bold text-emerald-600">{formatCurrency(data.by_status?.paid?.total || 0)}</p>
              <p className="text-xs text-zinc-500">Paid</p>
            </div>
            <div className="text-center p-3 bg-white rounded-lg">
              <p className="text-2xl font-bold text-amber-600">{formatCurrency(data.by_status?.pending?.total || 0)}</p>
              <p className="text-xs text-zinc-500">Pending</p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-3">
            <p className="text-sm font-medium text-zinc-700 mb-2">Monthly Invoices</p>
            <MiniBarChart data={data.by_month || []} valueKey="total" labelKey="month" color="#6366f1" />
          </div>
          <div className="bg-white rounded-lg p-3">
            <p className="text-sm font-medium text-zinc-700 mb-2">Status Breakdown</p>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(data.by_status || {}).map(([status, statusData]) => (
                <div key={status} className="text-center p-2 bg-zinc-50 rounded">
                  <p className="text-xs text-zinc-500 capitalize">{status}</p>
                  <p className="font-semibold text-zinc-900">{statusData.count}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Monthly Jobs Report (with date filter)
const MonthlyJobsReport = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fromDate, setFromDate] = useState(defaultFromDate);
  const [toDate, setToDate] = useState(defaultToDate);

  const loadData = useCallback(async (from, to) => {
    setIsLoading(true);
    try {
      const token = api.getToken();
      const startDate = format(from, 'yyyy-MM-dd');
      const endDate = format(to, 'yyyy-MM-dd');
      const res = await fetch(`${API_URL}/api/reports/monthly-jobs?start_date=${startDate}&end_date=${endDate}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setData(await res.json());
    } catch (err) {
      console.error('Error loading monthly jobs:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadData(fromDate, toDate); }, []);

  const handleApply = () => loadData(fromDate, toDate);

  return (
    <div className="space-y-4">
      <ReportDatePicker
        fromDate={fromDate}
        toDate={toDate}
        onFromChange={setFromDate}
        onToChange={setToDate}
        onApply={handleApply}
        isLoading={isLoading}
      />
      
      {isLoading ? (
        <div className="flex justify-center p-4"><Loader2 className="animate-spin text-zinc-400" /></div>
      ) : !data ? (
        <p className="text-zinc-500 text-sm">No data available</p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="text-center p-3 bg-white rounded-lg">
              <p className="text-2xl font-bold text-teal-600">{data.total_jobs}</p>
              <p className="text-xs text-zinc-500">Total Jobs</p>
            </div>
            <div className="text-center p-3 bg-white rounded-lg">
              <p className="text-2xl font-bold text-emerald-600">{data.total_completed}</p>
              <p className="text-xs text-zinc-500">Completed</p>
            </div>
            <div className="text-center p-3 bg-white rounded-lg">
              <p className="text-2xl font-bold text-blue-600">{data.average_completion_rate}%</p>
              <p className="text-xs text-zinc-500">Avg Completion</p>
            </div>
            <div className="text-center p-3 bg-white rounded-lg">
              <p className="text-2xl font-bold text-purple-600">{formatCurrency(data.total_revenue)}</p>
              <p className="text-xs text-zinc-500">Total Revenue</p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-3">
            <p className="text-sm font-medium text-zinc-700 mb-2">Jobs Completed by Month</p>
            <MiniBarChart data={data.months || []} valueKey="completed" labelKey="month" color="#14b8a6" />
          </div>
          <div className="bg-white rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50">
                <tr>
                  <th className="text-left p-2 font-medium text-zinc-600">Month</th>
                  <th className="text-right p-2 font-medium text-zinc-600">Total</th>
                  <th className="text-right p-2 font-medium text-zinc-600">Done</th>
                  <th className="text-right p-2 font-medium text-zinc-600">Rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {data.months?.slice(-6).reverse().map((m, idx) => (
                  <tr key={m.month || idx} className="hover:bg-zinc-50">
                    <td className="p-2 text-zinc-900">{m.month}</td>
                    <td className="p-2 text-right text-zinc-600">{m.total}</td>
                    <td className="p-2 text-right text-emerald-600">{m.completed}</td>
                    <td className="p-2 text-right text-zinc-600">{m.completion_rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

// Report Card Component
const ReportCard = ({ report, isExpanded, onToggle }) => {
  const Icon = report.icon;
  
  return (
    <div className="bg-white rounded-xl shadow-sm border border-zinc-100 overflow-hidden" data-testid={`report-${report.id}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-zinc-50 transition-colors"
        data-testid={`report-toggle-${report.id}`}
      >
        <div className="flex items-center gap-3">
          <div className={`p-2.5 rounded-xl ${report.bgColor}`}>
            <Icon size={20} className={report.iconColor} />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-zinc-900">{report.name}</h3>
            <p className="text-xs text-zinc-500">{report.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {report.hasDateFilter && (
            <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Date Filter</span>
          )}
          <ChevronDown 
            size={20} 
            className={`text-zinc-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
          />
        </div>
      </button>
      
      {isExpanded && (
        <div className="border-t border-zinc-100 p-4 bg-zinc-50">
          {report.component}
        </div>
      )}
    </div>
  );
};

// Main Reporting Component

/**
 * Customise Active-Customers Excel Export
 * — pick columns, hit Download. Hits POST /api/export/active-customers-xlsx.
 */
const ALL_EXPORT_COLUMNS = [
  { key: 'name', label: 'Name', defaultOn: true },
  { key: 'address', label: 'Address', defaultOn: true },
  { key: 'phone', label: 'Phone', defaultOn: true },
  { key: 'email', label: 'Email', defaultOn: true },
  { key: 'round', label: 'Round', defaultOn: true },
  { key: 'frequency', label: 'Frequency', defaultOn: true },
  { key: 'price', label: 'Price', defaultOn: true },
  { key: 'last_done', label: 'Last Done', defaultOn: true },
  { key: 'next_due', label: 'Next Due', defaultOn: true },
  { key: 'notes', label: 'Notes', defaultOn: true },
  { key: 'tags', label: 'Tags', defaultOn: false },
  { key: 'zone', label: 'Zone', defaultOn: false },
  { key: 'assignee', label: 'Worker', defaultOn: false },
  { key: 'balance', label: 'Balance', defaultOn: false },
  { key: 'payment_method', label: 'Payment Method', defaultOn: false },
];

const CustomiseExportModal = ({ apiUrl, token, onClose }) => {
  const [selected, setSelected] = useState(() => {
    const s = new Set();
    ALL_EXPORT_COLUMNS.forEach(c => { if (c.defaultOn) s.add(c.key); });
    return s;
  });
  const [downloading, setDownloading] = useState(false);

  const toggle = (key) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const download = async () => {
    if (selected.size === 0) { alert('Pick at least one column.'); return; }
    setDownloading(true);
    try {
      const columns = ALL_EXPORT_COLUMNS.filter(c => selected.has(c.key)).map(c => c.key);
      const res = await fetch(`${apiUrl}/api/export/active-customers-xlsx`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        cache: 'no-store',
        body: JSON.stringify({ columns }),  // server auto-handles one-off rounds
      });
      if (!res.ok) {
        let detail = `${res.status}`;
        try {
          const cloned = res.clone();
          const text = await cloned.text();
          if (text) detail = text.slice(0, 200);
        } catch { /* ignore */ }
        alert(`Export failed: ${detail}`);
        return;
      }
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition') || '';
      const m = /filename=([^;]+)/i.exec(cd);
      const filename = m ? m[1].trim().replace(/"/g, '') : `streakfree_active_customers_${Date.now()}.xlsx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      onClose();
    } catch (e) {
      alert(`Export failed: ${e.message || e}`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="customise-export-modal"
    >
      <div
        className="bg-white w-full sm:max-w-lg modal-shell-h rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-5 bg-gradient-to-br from-emerald-600 to-teal-700 text-white">
          <h2 className="text-xl font-bold">Customise Excel Export</h2>
          <p className="text-emerald-50 text-sm mt-1">Active customers · Tab 1 alphabetical · Tab 2 by round with totals</p>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          <div>
            <p className="text-xs uppercase tracking-wide text-zinc-500 font-semibold mb-2">Columns to include</p>
            <div className="grid grid-cols-2 gap-2">
              {ALL_EXPORT_COLUMNS.map(col => {
                const on = selected.has(col.key);
                return (
                  <label
                    key={col.key}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                      on ? 'border-emerald-500 bg-emerald-50' : 'border-zinc-200 bg-white hover:bg-zinc-50'
                    }`}
                    data-testid={`export-col-${col.key}`}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => toggle(col.key)}
                      className="rounded text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="text-sm text-zinc-800">{col.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
          <div className="rounded-xl bg-zinc-50 border border-zinc-200 p-3 text-xs text-zinc-600">
            <p className="font-semibold text-zinc-800 mb-1">One-off jobs are filtered automatically</p>
            <ul className="space-y-1">
              <li>· <span className="font-medium">"Perfect Properties"</span> on its own → skipped (one-off only).</li>
              <li>· <span className="font-medium">"Perfect Properties and Worthing North"</span> → included in <em>Worthing North</em>.</li>
              <li>· <span className="font-medium">Any "- one off"</span> round (e.g. "Goring - one off") → skipped.</li>
            </ul>
          </div>
          <div className="rounded-xl bg-zinc-50 border border-zinc-200 p-3 text-xs text-zinc-600">
            <p className="font-semibold text-zinc-800 mb-1">Tab layout</p>
            <ul className="space-y-1">
              <li>· <span className="font-medium">Tab 1 "All Customers":</span> every active customer (with a recurring round), alphabetical by first name.</li>
              <li>· <span className="font-medium">Tab 2 "By Round":</span> each round (Worthing North, Wick A, Wick B…) with totals — avg job price, monthly &amp; annual revenue.</li>
              <li>· <span className="font-medium">Grand total</span> at the very bottom across all rounds.</li>
            </ul>
          </div>
        </div>
        <div className="p-4 border-t border-zinc-200 flex gap-3">
          <button
            onClick={onClose}
            data-testid="customise-cancel"
            className="flex-1 py-3 bg-zinc-100 text-zinc-700 rounded-2xl font-semibold hover:bg-zinc-200"
          >
            Cancel
          </button>
          <button
            onClick={download}
            disabled={downloading || selected.size === 0}
            data-testid="customise-download"
            className="flex-[2] py-3 bg-gradient-to-br from-emerald-600 to-teal-700 text-white rounded-2xl font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:from-emerald-700 hover:to-teal-800 flex items-center justify-center gap-2"
          >
            {downloading ? (<><Loader2 size={16} className="animate-spin" /> Generating…</>) : (<><Download size={16} /> Download .xlsx</>)}
          </button>
        </div>
      </div>
    </div>
  );
};


const Reporting = ({ onMenuClick }) => {
  const [expandedReport, setExpandedReport] = useState(null);
  const [quickStats, setQuickStats] = useState(null);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [showCustomiseModal, setShowCustomiseModal] = useState(false);

  // Build the next-4-weeks window starting from this week's Monday, hit the
  // /api/export/work-planner-xlsx endpoint, download the file.
  const handlePlannerExport = async () => {
    const token = api.getToken();
    setExporting('__planner__');
    try {
      const today = new Date();
      // This week's Monday (1 = Mon, 0 = Sun)
      const dow = today.getDay();
      const daysFromMon = (dow === 0) ? -6 : (1 - dow);
      const monday = new Date(today);
      monday.setDate(today.getDate() + daysFromMon);
      const end = new Date(monday);
      end.setDate(monday.getDate() + 27);  // 4 weeks inclusive = Mon to Sun x 4
      const fmt = (d) => d.toISOString().slice(0, 10);
      const url = `${API_URL}/api/export/work-planner-xlsx?start_date=${fmt(monday)}&end_date=${fmt(end)}`;
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
        cache: 'no-store',
      });
      if (!res.ok) {
        let detail = `${res.status}`;
        try { const cloned = res.clone(); const t = await cloned.text(); if (t) detail = t.slice(0, 200); } catch { /* ignore */ }
        alert(`Planner export failed: ${detail}`);
        return;
      }
      const blob = await res.blob();
      let filename = `work_planner_${fmt(monday)}_to_${fmt(end)}.xlsx`;
      const cd = res.headers.get('Content-Disposition') || '';
      const m = /filename=([^;]+)/i.exec(cd);
      if (m) filename = m[1].trim().replace(/"/g, '');
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      alert(`Planner export failed: ${e.message || e}`);
    } finally {
      setExporting(null);
    }
  };

  // Streams a CSV/zip from one of the /api/export endpoints and triggers a
  // browser download.
  const handleExport = async (kind) => {
    const token = api.getToken();
    setExporting(kind);
    try {
      const res = await fetch(`${API_URL}/api/export/${kind}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        // Bypass any offline service-worker cache — these exports must be live.
        cache: 'no-store',
      });
      if (!res.ok) {
        // Clone before reading: a SW or other middleware may have peeked at
        // the body, which would otherwise cause "body stream already read".
        let detail = `${res.status}`;
        try {
          const cloned = res.clone();
          const text = await cloned.text();
          if (text) detail = text.slice(0, 200);
        } catch { /* ignore — keep generic detail */ }
        alert(`Export failed: ${detail}`);
        return;
      }
      const blob = await res.blob();
      let filename = `streakfree_${kind}_${Date.now()}.csv`;
      const cd = res.headers.get('Content-Disposition') || '';
      const m = /filename=([^;]+)/i.exec(cd);
      if (m) filename = m[1].trim().replace(/"/g, '');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(`Export failed: ${e.message || e}`);
    } finally {
      setExporting(null);
    }
  };

  // Load quick stats for the header (current year)
  useEffect(() => {
    const loadQuickStats = async () => {
      setIsLoadingStats(true);
      const token = api.getToken();
      
      // Get current month dates for signups
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      const monthStartStr = format(monthStart, 'yyyy-MM-dd');
      const monthEndStr = format(monthEnd, 'yyyy-MM-dd');
      
      try {
        const headers = { 'Authorization': `Bearer ${token}` };
        const [activeRes, recurringRes, growthRes, churnRes] = await Promise.all([
          fetch(`${API_URL}/api/reports/active-customers`, { headers }).catch(() => ({ ok: false })),
          fetch(`${API_URL}/api/reports/job-revenue-summary`, { headers }).catch(() => ({ ok: false })),
          fetch(`${API_URL}/api/reports/customer-growth?months=1&start_date=${monthStartStr}&end_date=${monthEndStr}&exclude_bulk_imports=true`, { headers }).catch(() => ({ ok: false })),
          fetch(`${API_URL}/api/reports/customer-churn`, { headers }).catch(() => ({ ok: false }))
        ]);

        const stats = {};
        if (activeRes.ok) stats.active = await activeRes.json();
        if (recurringRes.ok) stats.recurring = await recurringRes.json();
        if (growthRes.ok) stats.growth = await growthRes.json();
        if (churnRes.ok) stats.churn = await churnRes.json();
        setQuickStats(stats);
      } catch (err) {
        console.error('Error loading quick stats:', err);
      } finally {
        setIsLoadingStats(false);
      }
    };
    loadQuickStats();
  }, []);

  // Report definitions
  const reports = [
    {
      id: 'active-customers',
      name: 'Active Customers',
      description: 'Current customer list with job counts',
      icon: Users,
      bgColor: 'bg-blue-100',
      iconColor: 'text-blue-600',
      hasDateFilter: false,
      component: <ActiveCustomersReport />
    },
    {
      id: 'customer-growth',
      name: 'Customer Signups & Cancellations',
      description: 'Monthly customer acquisition with bulk import detection',
      icon: UserPlus,
      bgColor: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      hasDateFilter: true,
      component: <CustomerGrowthReport />
    },
    {
      id: 'customer-churn',
      name: 'Customer Churn Rate',
      description: 'Customer retention metrics',
      icon: UserMinus,
      bgColor: 'bg-red-100',
      iconColor: 'text-red-600',
      hasDateFilter: false,
      component: <CustomerChurnReport />
    },
    {
      id: 'total-revenue',
      name: 'Total Job Revenue',
      description: 'Earnings overview and breakdown',
      icon: PoundSterling,
      bgColor: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      hasDateFilter: true,
      component: <TotalRevenueReport />
    },
    {
      id: 'revenue-by-worker',
      name: 'Job Revenue by Worker',
      description: 'Individual worker performance',
      icon: Briefcase,
      bgColor: 'bg-purple-100',
      iconColor: 'text-purple-600',
      hasDateFilter: true,
      component: <RevenueByWorkerReport />
    },
    {
      id: 'owing-customers',
      name: 'Owing Customers',
      description: 'Outstanding customer balances',
      icon: AlertCircle,
      bgColor: 'bg-amber-100',
      iconColor: 'text-amber-600',
      hasDateFilter: false,
      component: <OwingCustomersReport />
    },
    {
      id: 'payments',
      name: 'Payments by Date Range',
      description: 'Track incoming payments',
      icon: CreditCard,
      bgColor: 'bg-green-100',
      iconColor: 'text-green-600',
      hasDateFilter: true,
      component: <PaymentsReport />
    },
    {
      id: 'invoices',
      name: 'Invoices by Date Range',
      description: 'Invoice history and status',
      icon: Receipt,
      bgColor: 'bg-indigo-100',
      iconColor: 'text-indigo-600',
      hasDateFilter: true,
      component: <InvoicesReport />
    },
    {
      id: 'monthly-jobs',
      name: 'Monthly Jobs Completed',
      description: 'Job activity summary by month',
      icon: CheckCircle,
      bgColor: 'bg-teal-100',
      iconColor: 'text-teal-600',
      hasDateFilter: true,
      component: <MonthlyJobsReport />
    }
  ];

  if (isLoadingStats) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500 mx-auto mb-2" />
          <p className="text-zinc-500">Loading reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-zinc-100" data-testid="reporting-page">
      {/* Header */}
      <div className="bg-zinc-900 text-white sticky top-0 z-30" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="flex items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3">
            <button 
              onClick={onMenuClick}
              className="p-2 -ml-2 hover:bg-white/10 rounded-xl transition-colors"
              data-testid="menu-button"
            >
              <Menu size={22} strokeWidth={1.5} />
            </button>
            <div>
              <h1 className="text-xl font-bold tracking-tight">Reports</h1>
              <p className="text-xs text-zinc-400">Showing {currentYear} data by default</p>
            </div>
          </div>
          <div className="flex items-center gap-2 relative">
            <button 
              onClick={() => window.location.reload()}
              className="p-2.5 hover:bg-white/10 rounded-xl transition-colors"
              data-testid="refresh-btn"
            >
              <RefreshCw size={20} />
            </button>
            <button 
              onClick={() => setShowExportMenu(s => !s)}
              className="p-2.5 hover:bg-white/10 rounded-xl transition-colors"
              data-testid="export-btn"
            >
              <Download size={20} />
            </button>
            {showExportMenu && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setShowExportMenu(false)} />
                <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-2xl shadow-2xl border border-zinc-200 overflow-hidden z-40">
                  <div className="px-4 py-2 border-b border-zinc-100 bg-zinc-50">
                    <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">Download as CSV</p>
                  </div>
                  {[
                    { id: 'active-customers', label: 'Active customers (CSV, default)', sub: 'name, address, phone, email, frequency, price, last done, next due, notes' },
                    { id: '__customise__', label: 'Active customers (Excel, customisable) ✨', sub: 'Pick columns · Tab 1 alphabetical · Tab 2 by round + totals' },
                    { id: '__planner__', label: 'Work Planner — next 4 weeks (Excel)', sub: 'Mon-Sun for 4 weeks, one tab per day + summary' },
                    { id: 'customers', label: 'All customers', sub: 'Full customer details + Stripe/GC IDs' },
                    { id: 'jobs', label: 'All jobs', sub: 'Every scheduled and completed job' },
                    { id: 'invoices', label: 'All invoices', sub: 'Every invoice ever issued' },
                  ].map(opt => (
                    <button
                      key={opt.id}
                      onClick={() => {
                        setShowExportMenu(false);
                        if (opt.id === '__customise__') {
                          setShowCustomiseModal(true);
                        } else if (opt.id === '__planner__') {
                          handlePlannerExport();
                        } else {
                          handleExport(opt.id);
                        }
                      }}
                      data-testid={`export-${opt.id}`}
                      className="w-full text-left px-4 py-3 hover:bg-emerald-50 transition-colors border-b border-zinc-50 last:border-0"
                    >
                      <div className="text-sm font-semibold text-zinc-900 flex items-center justify-between">
                        {opt.label}
                        {exporting === opt.id && <Loader2 size={14} className="animate-spin text-emerald-600" />}
                      </div>
                      <div className="text-xs text-zinc-500 mt-0.5">{opt.sub}</div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="px-4 py-4 bg-white border-b border-zinc-200">
        <p className="text-xs text-zinc-500 mb-2">Quick Stats</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard 
            title="Active Customers" 
            value={quickStats?.active?.total_active || 0} 
            icon={Users} 
            color="blue"
          />
          <StatCard 
            title="Avg Monthly Recurring" 
            value={formatCurrency(quickStats?.recurring?.recurring_monthly || 0)} 
            icon={PoundSterling} 
            color="emerald"
            subtitle={`${quickStats?.recurring?.recurring_count || 0} recurring jobs`}
          />
          <StatCard 
            title="Signups This Month" 
            value={quickStats?.growth?.total_signups || 0} 
            subtitle={quickStats?.growth?.total_cancellations > 0 ? `${quickStats?.growth?.total_cancellations} cancelled` : 'No cancellations'}
            icon={UserPlus} 
            color="emerald"
          />
          <StatCard 
            title="Churn Rate" 
            value={`${quickStats?.churn?.churn_rate || 0}%`} 
            subtitle={`${quickStats?.churn?.retention_rate || 0}% retained`}
            icon={UserMinus} 
            color={quickStats?.churn?.churn_rate > 5 ? 'red' : 'emerald'}
          />
        </div>
      </div>

      {/* Reports List */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-3 max-w-4xl mx-auto">
          <h2 className="text-sm font-semibold text-zinc-500 uppercase tracking-wider px-1">
            Available Reports
          </h2>
          {reports.map(report => (
            <ReportCard
              key={report.id}
              report={report}
              isExpanded={expandedReport === report.id}
              onToggle={() => setExpandedReport(expandedReport === report.id ? null : report.id)}
            />
          ))}
        </div>
      </div>

      {/* Customise XLSX Export modal */}
      {showCustomiseModal && (
        <CustomiseExportModal
          apiUrl={API_URL}
          token={api.getToken()}
          onClose={() => setShowCustomiseModal(false)}
        />
      )}
    </div>
  );
};

export default Reporting;
